import { CellHandle, CellTarget, type SheetRow } from "@tento-lona";
import { Actor, type Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { ColumnCheckmarkRow } from "@tento-lona/sheets/platform";
import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { CheckSquareIcon, CheckmarkIcon, LonaIcons } from "@tento-ui/ui/icons";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import {
  type BindCellContext,
  RenderPlugin,
  RowOptionsElement,
  type SheetLegendClass,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { HomeCardFactory } from "@tento-lona/sheets-ui/home-card/index.ts";
import { SheetRowCheckmarkCard } from "./column-checkmark/support/checkmark-card.ts";
import { COLUMN_CHECKMARK_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { storageCellDateForColumn } from "./cell-date.ts";
import { CellCommitBaseline } from "./cell-commit-baseline.ts";

/** The renderer plugin for a checkmark row: one toggle per column. */
export class ColumnCheckmarkRendererPlugin implements RenderPlugin.Renderer<
  CheckmarkCell,
  SheetLegend.DefaultRow,
  ColumnCheckmarkRow
> {
  static readonly spec: PerTypeSpec = COLUMN_CHECKMARK_SPEC;

  readonly role = "renderer";
  readonly id = "column.checkmark";
  readonly rowClass: typeof ColumnCheckmarkRow = ColumnCheckmarkRow;
  readonly icon: HTMLTemplateElement = LonaIcons.$template(CheckSquareIcon);

  #cellHandles = new Map<CheckmarkCell, CellHandle>();
  /** A fresh token on every bind, including same-type column reuse. */
  #cellBindings = new WeakMap<CheckmarkCell, object>();
  constructor(
    public readonly makeRowOptions: RowOptionsElement.Factory = RowOptionsElement.Factory
      .NOOP,
  ) {}

  #disposeHandleForCell($cell: CheckmarkCell): void {
    const existing = this.#cellHandles.get($cell);
    if (existing) {
      existing.dispose();
      this.#cellHandles.delete($cell);
    }
  }

  onCellRecycled($cell: CheckmarkCell): void {
    this.#cellBindings.delete($cell);
    this.#disposeHandleForCell($cell);
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Use checkmarks to track weekly habits like taking out the trash or doing laundry.";
  }

  makeCell(): CheckmarkCell {
    return CheckmarkCell.make();
  }

  async bindCell(
    $cell: CheckmarkCell,
    { row, sheetRow, column, inThePast }: BindCellContext<ColumnCheckmarkRow>,
  ) {
    this.#disposeHandleForCell($cell);
    const binding = {};
    this.#cellBindings.set($cell, binding);
    const isCurrent = () => this.#cellBindings.get($cell) === binding;

    const checkColor = row.checkColor;
    const cellDate = storageCellDateForColumn(sheetRow.timescaleSetting, column);
    // This actor belongs to this binding's writes. A later bind to the same
    // row and column must hear an earlier binding's accepted write as data;
    // a shared logical-cell actor would suppress that echo and leave stale paint.
    const cellActor = new Actor(`column.checkmark:${sheetRow.id}:${column}`);

    const period = inThePast ? "past" : "current-or-future";
    // A checkmark row has one cell per column: its single-cardinality
    // facade writes it (`null`: a row of another cardinality, or a computed one).
    const cells = sheetRow.writableSingle();
    const committed = new CellCommitBaseline<boolean>();
    let checked = false;
    const paint = () => $cell.bind(checked ? checkColor : null, period);
    const reconcile = () => {
      if (!isCurrent()) return;
      const next = committed.value ?? false;
      if (next === checked) return;
      checked = next;
      paint();
    };
    const bind = (data: number) => {
      checked = data === 1;
      committed.bind(checked);
      paint().addPointerEvent({
        onClick: () => {
          if (!isCurrent() || !row.editable || cells == null) return;
          checked = !checked;
          const write = committed.begin(checked);
          paint();
          // Each verdict settles its own write. A refused predecessor cannot
          // undo a newer pending or accepted toggle, even when both have the
          // same boolean value. A recycled cell cannot be repainted by a
          // previous column's late verdict.
          cells.write(CellTarget.date(cellDate), checked, cellActor).then(
            () => {
              committed.accepted(write);
              reconcile();
            },
            (err: unknown) => {
              committed.refused(write);
              Tracing.warn("net")("column-checkmark/write failed", {
                rowId: sheetRow.id.toString(),
                error: String(err),
              });
              if (!isCurrent()) return;
              checked = committed.value ?? false;
              paint();
            },
          );
        },
      });
    };

    // Same-type cells are recycled in place across columns (horizontal
    // scroll) without `onCellRecycled` firing, and `onData` below is
    // deferred to a microtask — so until it fires, a recycled cell's
    // live `onClick` still targets the PREVIOUS column's week. A fast
    // click in that window persisted the toggle to the wrong week
    // (same hazard `983f2c5` fixed for column.text). Install this
    // column's handler synchronously from the cached value; `onData`
    // then re-binds idempotently (`addPointerEvent` replaces, never
    // stacks).
    const single = sheetRow.single();
    if (single == null) return;
    bind(Number(single.peek(cellDate)?.value ?? 0));

    const handle = single.cell(cellDate);
    this.#cellHandles.set($cell, handle);
    handle.onLoading(() => {
      if (isCurrent()) $cell.bind(null, period);
    });
    handle.onData((data, change) => {
      if (!isCurrent()) return;
      // The optimistic paint already reflects this cell's own write; keep
      // its baseline fresh without rebinding or flickering.
      if (change?.actor === cellActor) {
        committed.echo(data?.value === 1);
        return;
      }
      bind(Number(data?.value ?? 0));
    });
  }

  onResize(
    _$cell: CheckmarkCell,
    _context: BindCellContext<ColumnCheckmarkRow>,
  ): void {}

  loadData = null;
  legendFactory: Option<SheetLegendClass<SheetLegend.DefaultRow>> = null;

  onBindRow(_sheet: SheetDelegate, _row: SheetRow.Joined.Handle): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  homeCardFactory: HomeCardFactory<SheetRowCheckmarkCard> = {
    make: (ctx) => ({
      $cardContent: SheetRowCheckmarkCard.makeWith({
        onToggle: (checked) => {
          ctx.onCardChecked?.(checked);
          const cells = ctx.row.writableSingle();
          if (cells == null) return;
          cells.writeDurable(CellTarget.date(ctx.datePartial), checked ? 1 : 0, Actor.EXTERNAL).settled
            .catch((err: unknown) =>
              Tracing.warn("net")("column-checkmark/card write failed", { error: String(err) })
            );
        },
      }),
      $listContent: null,
      $listAccessory: null,
    }),

    bind: (content, ctx) => {
      const checked = ctx.row.single()?.peek(ctx.datePartial)?.value === 1;
      content.$cardContent.setChecked(checked);
      ctx.onCardChecked?.(checked);
    },
  };
}

/** One checkmark cell. */
@component({
  name: "std-checkmark-cell",
})
export class CheckmarkCell extends LonaWebComponent {
  /**
   * Paint the cell: `bg` when checked (`null` unchecked), and the unchecked
   * underlay by `period` — a past column's is dimmer.
   */
  bind(bg: Option<string>, period: "past" | "current-or-future"): CheckmarkCell {
    this.assignStyles({
      color: bg ? "var(--text-pri-inv)" : "var(--fg-icon-sec)",
      "--underlay-color": bg
        ? bg
        : period === "current-or-future"
          ? "var(--bg-surface-light)"
          : "var(--bg-hover-very-light)",
    });
    return this;
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        position: relative;
        height: 100%;
        cursor: pointer;
        margin: 1px 2px;
        border-radius: 8px;
        background: var(--underlay-color);
      }

      :host(:hover)::after {
        position: absolute;
        content: "";
        inset: 0;
        border-radius: 8px;
        background: var(--bg-hover);
        pointer-events: none;
      }

      #root {
        height: 100%;
        align-items: center;
        justify-content: center;
      }

      #icon {
        color: inherit;
      }

      :host([selected]) {
      }
    `,
  ];

  static $icons: Option<string[]> = [LonaIcons.symbol(CheckmarkIcon)];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <svg id="icon" height="20" width="20">
        <use href="#checkmark-icon" />
      </svg>
    </std-col>
  `;
}

/** Types that go with `ColumnCheckmarkRendererPlugin`: `Attributes`. */
export namespace ColumnCheckmarkRendererPlugin {
  /**
   * Attribute bag for checkmark rows. Replaces `CheckmarkVariant.Type`
   * post-6f — same fields, read via `Json.string(row.attributes(), "checkColor")`.
   */
  export interface Attributes {
    checkColor?: string;
  }
}
